// Market Indices Configuration
// Hot-swappable configuration for dashboard market indices display

export interface MarketIndex {
  ticker: string;
  name: string;
  description: string;
  category: "major" | "sector" | "international" | "commodity";
  /**
   * FMP symbol of the CME e-mini future that carries this index's
   * out-of-hours movement — futures quotes and 1-min bars run ~24h while the
   * index itself stops computing at the close. Cards show the future's live
   * % (never its absolute level: an e-mini trades points off its index, and
   * NQUSD tracks the Nasdaq-100 rather than the Composite).
   */
  futuresTicker?: string;
  /**
   * Trading session for the 1-min chart filter, minutes since midnight in
   * the bars' NATIVE exchange wall-clock (FMP stamps intraday bars in local
   * exchange time — ET for US symbols, London time for FTSE). Defaults to
   * NYSE 9:30–16:00.
   */
  session?: { start: number; end: number };
}

// London session 08:00–16:30, in the London wall-clock FMP uses for FTSE bars.
const LSE_SESSION = { start: 8 * 60, end: 16 * 60 + 30 };

export const MARKET_INDICES: MarketIndex[] = [
  // Major US Indices
  {
    ticker: "^GSPC",
    name: "S&P 500",
    description: "Large-cap U.S. stock market index",
    category: "major",
    futuresTicker: "ESUSD",
  },
  {
    ticker: "^DJI",
    name: "Dow Jones",
    description: "30 large U.S. companies index",
    category: "major",
    futuresTicker: "YMUSD",
  },
  {
    ticker: "^IXIC",
    name: "NASDAQ",
    description: "Technology-heavy market index",
    category: "major",
    futuresTicker: "NQUSD",
  },
  {
    ticker: "^RUT",
    name: "Russell 2000",
    description: "Small-cap U.S. stock market index",
    category: "major",
    futuresTicker: "RTYUSD",
  },
  {
    ticker: "^VIX",
    name: "VIX",
    description: "CBOE Volatility Index - Market fear gauge",
    category: "major",
  },

  // International Indices
  {
    ticker: "^FTSE",
    name: "FTSE 100",
    description: "UK stock market index",
    category: "international",
    session: LSE_SESSION,
  },
  {
    ticker: "^FTMC",
    name: "FTSE 250",
    description: "UK mid-cap stock market index",
    category: "international",
    session: LSE_SESSION,
  },

  // Commodities
  {
    ticker: "CLUSD",
    name: "Oil",
    description: "WTI crude oil futures",
    category: "commodity",
  },
];

// Default indices to display (can be easily modified)
export const DEFAULT_DASHBOARD_INDICES = MARKET_INDICES.slice(0, 6); // Show first 6 indices

// Configuration for different display modes
export const DASHBOARD_CONFIG = {
  maxTilesPerRow: {
    desktop: 3,
    tablet: 2,
    mobile: 1,
  },
  tileDimensions: {
    width: 180,
    height: 160,
  },
  spacing: 2, // MUI spacing units
};
